// Core/base hooks for PracticeTest
import type { Question } from '../../types';
// Extracted to reduce file size and prevent Cursor crashes

import { useEffect } from 'react';
import { PracticeTestState, Action } from './PracticeTest.reducer';
import type { EffectsContext } from './PracticeTest.effects.types';

// Hook: Reset Tracking Refs
export const useResetTrackingRefs = (
  questions: Question[],
  currentQuestionIndex: number,
  spokenQuestionsRef: React.MutableRefObject<Set<number>>,
  wrongAnswersInRowRef: React.MutableRefObject<number>,
  recentMistakesRef: React.MutableRefObject<number>,
  recentMistakesInTopicRef: React.MutableRefObject<Record<string, number>>,
  lastCheckInTimeRef: React.MutableRefObject<number>,
  caseRuleMistakeCountsRef: React.MutableRefObject<Record<string, number>>,
  caseRuleAddressedRef: React.MutableRefObject<Record<string, number>>
) => {
  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex === 0) {
      spokenQuestionsRef.current.clear();
      wrongAnswersInRowRef.current = 0;
      recentMistakesRef.current = 0;
      recentMistakesInTopicRef.current = {};
      lastCheckInTimeRef.current = 0; // Reset cooldown
      caseRuleMistakeCountsRef.current = {};
      caseRuleAddressedRef.current = {};
    }
  }, [
    questions.length,
    currentQuestionIndex,
    spokenQuestionsRef,
    wrongAnswersInRowRef,
    recentMistakesRef,
    recentMistakesInTopicRef,
    lastCheckInTimeRef,
    caseRuleMistakeCountsRef,
    caseRuleAddressedRef
  ]);
};

// Hook: Body Class Management
export const useBodyClass = () => {
  useEffect(() => {
    document.body.classList.add('practice-test-active');
    document.documentElement.classList.add('practice-test-active');
    return () => {
      document.body.classList.remove('practice-test-active');
      document.documentElement.classList.remove('practice-test-active');
    };
  }, []);
};

// Hook: Keyboard Input Handler
export const useKeyboardHandler = (
  state: PracticeTestState,
  questions: Question[],
  testId: string | undefined,
  handleAnswer: (answerId: string) => void
) => {
  useEffect(() => {
    if (state.testComplete || questions.length === 0) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Only handle if not typing in input field
      if (document.activeElement?.tagName === 'INPUT' || 
          document.activeElement?.tagName === 'TEXTAREA' ||
          document.activeElement?.getAttribute('contenteditable') === 'true') {
        return;
      }

      // Don't handle if test is complete or no questions
      if (state.testComplete || questions.length === 0 || state.currentQuestionIndex >= questions.length) return;

      const currentQ = questions[state.currentQuestionIndex];
      if (!currentQ || state.isAnswered) return;

      const key = e.key.toUpperCase();
      
      if (['A', 'B', 'C', 'D'].includes(key)) {
        e.preventDefault();
        
        // Find the option with this letter
        if (currentQ.options) {
          const optionIndex = key.charCodeAt(0) - 65; // A=0, B=1, etc.
          if (optionIndex >= 0 && optionIndex < currentQ.options.length) {
            const answerId = currentQ.options[optionIndex].id;
            // Call handleAnswer directly
            if (!state.isAnswered) {
              handleAnswer(answerId);
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [state.testComplete, questions, state.currentQuestionIndex, state.isAnswered, testId, handleAnswer]);
};

// Hook: Reset Explanation Flag
export const useResetExplanationFlag = (
  currentQuestionIndex: number,
  setExplanationInChat: React.Dispatch<React.SetStateAction<boolean>>
) => {
  useEffect(() => {
    setExplanationInChat(false);
  }, [currentQuestionIndex, setExplanationInChat]);
};

// Hook: Explanation Watcher
export const useExplanationWatcher = (
  context: EffectsContext
) => {
  const { state: _state, currentQuestionIndex, isAnswered, showExplanation, setExplanationInChat } = context;
  
  useEffect(() => {
    if (!showExplanation || !isAnswered) {
      setExplanationInChat(false);
      return;
    }

    const explanationId = `explanation-${currentQuestionIndex}`;
    // Read from context.state to avoid dependency loop
    const explanationExists = context.state.chatMessages.some(msg => msg.id === explanationId);
    setExplanationInChat(explanationExists);
    // CRITICAL: state.chatMessages removed from deps to prevent infinite loop
    // We read it from context.state inside the effect to get latest value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExplanation, isAnswered, currentQuestionIndex, setExplanationInChat]);
};

// Hook: Timer Management
export const useTimer = (
  state: PracticeTestState,
  dispatch: React.Dispatch<Action>
) => {
  useEffect(() => {
    if (state.testComplete || !state.questionStartTime) return;
    
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - state.questionStartTime) / 1000);
      dispatch({ type: 'SET_TIME_LEFT', payload: Math.max(0, 90 - elapsed) }); // 1.5 min max
    }, 1000);
    
    return () => clearInterval(interval);
  }, [state.testComplete, state.questionStartTime, dispatch]);
};

// Hook: Difficulty Level Effect
export const useDifficultyLevel = (
  state: PracticeTestState,
  dispatch: React.Dispatch<Action>
) => {
  useEffect(() => {
    if (state.currentDifficultyLevel !== 3) {
      // Reset to default if needed
      dispatch({ type: 'SET_DIFFICULTY_LEVEL', payload: 3 });
    }
  }, [state.currentDifficultyLevel, dispatch]);
};
